import type { OwnerHomeStudio } from "./schemas/home.ts";

/**
 * The five studio numbers of owner Home (Studio Home H4), where clients book for themselves. The
 * server counts (`ownerHome`, `studio`); these are the words, so Desk's tiles and the phone's
 * counters say the same thing.
 */

/** The studio week starts on Monday (`Date.getDay`), in the business timezone. */
export const STUDIO_WEEK_START = 1;
/** Intro to package reads the intro offers bought in this many days before today. */
export const INTRO_TO_PACK_DAYS = 90;

export type StudioNumberKey = "fill" | "active" | "ending" | "intro" | "late";

export type StudioNumber = {
  key: StudioNumberKey;
  label: string;
  /** The big number: "64%", "38". */
  value: string;
  /** The words after it. */
  unit: string;
  /** The line under it. */
  note: string;
};

function percent(part: number, whole: number): number {
  return whole ? Math.round((part / whole) * 100) : 0;
}

/** The share of intro clients who bought a full package after the intro, 0 to 100, or null with no intro client. */
export function introToPackShare(studio: Pick<OwnerHomeStudio, "intro">): number | null {
  const total = studio.intro.clients.length;
  if (!total) return null;
  return percent(studio.intro.clients.filter((row) => row.packageName !== null).length, total);
}

/** The five numbers in the order of the tiles. `members` is the business word ("clients"). */
export function studioNumbers(studio: OwnerHomeStudio, words: { members: string }): StudioNumber[] {
  const { fill } = studio;
  const introShare = introToPackShare(studio);
  const converted = studio.intro.clients.filter((row) => row.packageName !== null).length;
  const endsIn14 = studio.endingSoon.length;
  return [
    {
      key: "fill",
      label: "Fill this week",
      value: fill.seats ? `${percent(fill.booked, fill.seats)}%` : "0%",
      unit: "full",
      note: fill.seats ? `${fill.booked} of ${fill.seats} places booked, Monday to Sunday` : "No class this week",
    },
    {
      key: "active",
      label: `Active ${words.members}`,
      value: String(studio.active.length),
      unit: words.members,
      note: "A current package, or a next booking",
    },
    {
      key: "ending",
      label: "Ending soon",
      value: String(endsIn14),
      // One row per client, as on the Clients page (review R1.1), so the unit is the business word.
      unit: words.members,
      note: "A package ends in 14 days, or 2 sessions left or fewer",
    },
    {
      key: "intro",
      label: "Intro to package",
      value: introShare === null ? "None" : `${introShare}%`,
      unit: introShare === null ? "" : "bought a package",
      note: studio.intro.clients.length
        ? `${converted} of ${studio.intro.clients.length} intro ${studio.intro.clients.length === 1 ? "offer" : "offers"} in the last ${studio.intro.windowDays} days`
        : `No intro offer bought in the last ${studio.intro.windowDays} days`,
    },
    {
      key: "late",
      label: "Late cancels",
      value: String(studio.lateCancels.length),
      unit: "this week",
      note: "Cancelled after the cut-off, Monday to Sunday",
    },
  ];
}
